<script setup>

import VideoFadeTab from "@/components/editor/VideoFadeTab.vue";
import WatermarkTab from "@/components/editor/WatermarkTab.vue";
import TextOverlaysTab from "@/components/editor/TextOverlaysTab.vue";
import AudioFadeTab from "@/components/editor/AudioFadeTab.vue";
import {computed, onMounted, ref, watch} from "vue";
import store from "@/vuex/store";
import EditorTemplates from "@/components/EditorTemplates.vue";
import {getClipsDuration} from "@/helpers";

const watermark_positions = ref([
  [
    ['top', 'left'],
    ['top', 'center'],
    ['top', 'right']
  ],
  [
    ['center', 'left'],
    ['center', 'center'],
    ['center', 'right']
  ],
  [
    ['bottom', 'left'],
    ['bottom', 'center'],
    ['bottom', 'right']
  ]
])

const ffmpegData = ref({
  video_url: null,
  video_duration: 0,
  has_watermark: false,
  watermark_position: ['top', 'right'],
  watermark_horizontal_m: 15,
  watermark_vertical_m: 15,
  watermark_scale_h: 0,
  watermark_scale_w: 0,
  trim_interval: [],
  watermark_interval: [0, 0],
  watermark_image: null,
  cut_out_interval: [],
  fade_in_model: {
    active: false,
    time: 1,
    color: '#000000',
  },
  fade_out_model: {
    active: false,
    time: 1,
    color: '#000000',
  },
  a_fade_in_model: {
    active: false,
    time: 1,
  },
  a_fade_out_model: {
    active: false,
    time: 1,
  },
  text_overlays: [],
})

const image = ref(null)
const stream = computed(() => store.getters["live/getStream"])
const isFadeSyncWithVideo = ref(false)
const renderKey = ref(0)
const clips = computed(() => store.getters["live/getClips"])
const clipsDuration = computed(() => store.getters["live/getClipsDuration"])
const loading = ref(true)

onMounted(() => {
  if (stream.value.options) {
    ffmpegData.value = Object.assign(ffmpegData.value, stream.value.options);
    image.value = stream.value.options.watermark_image
  }
  ffmpegData.value.video_duration = clipsDuration.value
  loading.value = false
})

function templateChanged(options) {
  ffmpegData.value = Object.assign(ffmpegData.value, options)
  image.value = options?.watermark_image || null
  renderKey.value++
}

function updateImage(e) {
  ffmpegData.value.watermark_image = e
}


watch(clips, () => {
  ffmpegData.value.video_duration = clipsDuration.value
  ffmpegData.value.watermark_interval = [ffmpegData.value.watermark_interval[0] || 0, clipsDuration.value]
  ffmpegData.value.trim_interval = [0, clipsDuration.value]
}, {deep: true})

watch(ffmpegData.value.fade_in_model, (val) => {
  if (isFadeSyncWithVideo.value === true) {
    Object.assign(ffmpegData.value.a_fade_in_model, val);
  }
}, {deep: true})

watch(ffmpegData.value.fade_out_model, (val) => {
  if (isFadeSyncWithVideo.value === true) {
    Object.assign(ffmpegData.value.a_fade_out_model, val);
  }
}, {deep: true})

watch(ffmpegData.value.a_fade_in_model, (val) => {
  if (isFadeSyncWithVideo.value === true) {
    Object.assign(ffmpegData.value.fade_in_model, val);
  }
}, {deep: true})

watch(ffmpegData.value.a_fade_out_model, (val) => {
  if (isFadeSyncWithVideo.value === true) {
    Object.assign(ffmpegData.value.fade_out_model, val);
  }
}, {deep: true})

watch(isFadeSyncWithVideo, v => {
  if(v) {
    Object.assign(ffmpegData.value.a_fade_in_model, ffmpegData.value.fade_in_model);
    Object.assign(ffmpegData.value.a_fade_out_model, ffmpegData.value.fade_out_model);
  }
})

watch(ffmpegData, (v) => {
  store.commit('live/setOptions', v)
}, {deep: true})

</script>


<template>
  <section class="mx-auto mt-3">
    <div v-if="ffmpegData && !loading" >
      <div class="flex gap-8">
        <div class="max-w-[400px] w-full">
          <h3 class="text-dark_blue font-bold mb-4">Watermark</h3>
          <WatermarkTab
            v-model:ffmpegData="ffmpegData"
            :watermarkImage="image"
            :watermarkPositions="watermark_positions"
            :key="renderKey"
            @update:watermarkImage="updateImage"/>
        </div>

        <div class="max-w-[250px] w-full">
          <h3 class="text-dark_blue font-bold mb-4">Video Fade</h3>
          <VideoFadeTab v-model:ffmpegData="ffmpegData"/>

          <h3 class="text-dark_blue font-bold mb-4 mt-8">Audio Fade</h3>
          <AudioFadeTab v-model:ffmpegData="ffmpegData" :useLocalTitle="false"
                        v-model:isFadeSyncWithVideo="isFadeSyncWithVideo"/>
        </div>

        <div class="max-w-[300px] w-full">
          <h3 class="text-dark_blue font-bold mb-4">Text Overlays</h3>
          <TextOverlaysTab
            v-model:textOverlays="ffmpegData.text_overlays"
            :videoDuration="ffmpegData.video_duration"
            :watermarkPositions="watermark_positions"
          />
        </div>

        <div class="max-w-[300px] w-full ml-auto">
          <EditorTemplates :options="ffmpegData" :watermark_image="ffmpegData.watermark_image"
                           @templateChanged="templateChanged"/>
        </div>

      </div>
    </div>
  </section>
</template>
